import { Button } from "@egaranti/components";
import { Tabs } from "@egaranti/components";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@egaranti/components";
import { TableSkeleton } from "@egaranti/components";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import useCampaignsStore from "@/stores/useCampaignsStore";

import ApplicationsTable from "@/components/campaigns/applicationsTable";
import EmptyTable from "@/components/shared/EmptyTable";
import TableHeaderWrapper from "@/components/shared/tableHeaderWrapper";

import { PlusIcon, SearchIcon } from "lucide-react";

import CheckRole from "@/components/shared/checkRole";

const Campaigns = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const tab = query.get("tab");

  const { campaigns, loading, filters, getCampaigns, setFilters } =
    useCampaignsStore();

  const [activeTab, setActiveTab] = useState(
    tab === "applications" ? 2 : 1,
  );

  useEffect(() => {
    if (activeTab === 1) {
      getCampaigns();
    }
  }, [activeTab]);

  const onClickItem = (e, id) => {
    e.stopPropagation();
    navigate(`/campaigns/${id}`);
  };

  return (
    <main className="min-h-full bg-[#F9FAFC] p-4">
      <div className="mb-4 flex items-center justify-between">
        <div></div>
        {activeTab === 1 && (
          <CheckRole section="CAMPAIGNS" action="EDIT">
            <Button onClick={() => navigate("/campaigns/create")}>
              <PlusIcon />
              <span>{t("campaignsPage.newCampaign", "Yeni Kampanya")}</span>
            </Button>
          </CheckRole>
        )}
      </div>

      <Tabs
        tabs={[
          { id: 1, title: t("campaignsPage.tabs.campaigns", "Kampanyalar") },
          { id: 2, title: t("campaignsPage.tabs.applications", "Başvurular") },
        ]}
        selectedTabId={activeTab}
        onTabChange={(id) => {
          setActiveTab(id);
          navigate(
            `/campaigns?tab=${id === 1 ? "campaigns" : "applications"}`,
          );
        }}
      />

      {activeTab === 1 && (
        <div className="rounded-lg">
          <div className="flex flex-col rounded-t-lg bg-white pb-4">
            <TableHeaderWrapper className="flex items-center justify-between gap-5">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder={t(
                    "campaignsPage.searchPlaceholder",
                    "Kampanya ara...",
                  )}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 pl-10 focus:border-blue-500 focus:outline-none"
                  onChange={(e) => {
                    const newFilters = { ...filters, query: e.target.value };
                    setFilters(newFilters);
                    getCampaigns();
                  }}
                />
                <SearchIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            </TableHeaderWrapper>

            {loading ? (
              <Table>
                <TableSkeleton colSpan={5} />
              </Table>
            ) : campaigns.length ? (
              <Table>
                <TableHeader>
                  <TableHead>
                    {t("campaignsPage.campaignName", "Kampanya Adı")}
                  </TableHead>
                  <TableHead>
                    {t("campaignsPage.startDate", "Başlangıç Tarihi")}
                  </TableHead>
                  <TableHead>
                    {t("campaignsPage.endDate", "Bitiş Tarihi")}
                  </TableHead>
                  <TableHead>
                    {t("campaignsPage.warrantyPeriod", "Garanti Süresi")}
                  </TableHead>
                  <TableHead>{t("campaignsPage.status", "Durum")}</TableHead>
                </TableHeader>
                <TableBody>
                  {campaigns?.map((campaign) => (
                    <TableRow
                      role="button"
                      onClick={(e) => onClickItem(e, campaign.id)}
                      key={campaign.id}
                    >
                      <TableCell className="font-medium">
                        {campaign.name}
                      </TableCell>
                      <TableCell>{campaign.startDate || "-"}</TableCell>
                      <TableCell>{campaign.endDate || "-"}</TableCell>
                      <TableCell>
                        {campaign.extendedWarrantyDurationMonths}{" "}
                        {t("campaignsPage.months", "Ay")}
                      </TableCell>
                      <TableCell>
                        <span
                          className={`text-sm ${campaign.isValid ? "text-green-600" : "text-gray-400"}`}
                        >
                          {campaign.isValid
                            ? t("campaignsPage.active", "Aktif")
                            : t("campaignsPage.passive", "Pasif")}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <EmptyTable
                title={t(
                  "campaignsPage.noCampaignsFound",
                  "Kampanya bulunamadı",
                )}
                desc={t(
                  "campaignsPage.noCampaignsFoundDesc",
                  "Henüz kampanya bulunmuyor",
                )}
              />
            )}
          </div>
        </div>
      )}

      {activeTab === 2 && <ApplicationsTable />}
    </main>
  );
};

export default Campaigns;
